import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AdminUserRow = {
  userId: number | string;
  emailMasked: string;
  email?: string;

  createdAt: string;

  role: string;
  isActive: boolean;

  plan: 'FREE' | 'PRO' | string;

  subscriptionStatus: string;
  nextBillingDate: string | null;

  telegramLinked: boolean;
};

export type AdminUsersStatusFilter =
    | 'all'
    | 'active'
    | 'canceled'
    | 'past_due';

export type AdminUsersQuery = {
  q: string;
  status: AdminUsersStatusFilter;
  page: number;
  size: number;
};

export type AdminUsersResponse = {
  items: AdminUserRow[];
  total: number;
  page: number;
  size: number;
  pageCount: number;
};

export type AdminUsersState = {
  items: AdminUserRow[];

  total: number;
  pageCount: number;

  loading: boolean;
  loaded: boolean;

  error: string | null;

  query: AdminUsersQuery;
};

export const DEFAULT_ADMIN_USERS_QUERY: AdminUsersQuery = {
  q: '',
  status: 'all',
  page: 1,
  size: 50,
};

function createInitialState(): AdminUsersState {
  return {
    items: [],

    total: 0,
    pageCount: 1,

    loading: false,
    loaded: false,

    error: null,

    query: {
      ...DEFAULT_ADMIN_USERS_QUERY,
    },
  };
}

const initialState = createInitialState();

function normalizeQuery(
    query: AdminUsersQuery,
): AdminUsersQuery {
  const page = Number(query.page);
  const size = Number(query.size);

  const normalizedStatus: AdminUsersStatusFilter =
      query.status === 'active' ||
      query.status === 'canceled' ||
      query.status === 'past_due'
          ? query.status
          : 'all';

  return {
    q: String(query.q ?? '').trim(),

    status: normalizedStatus,

    page:
        Number.isFinite(page) && page > 0
            ? Math.floor(page)
            : 1,

    size:
        Number.isFinite(size) && size > 0
            ? Math.min(
                200,
                Math.floor(size),
            )
            : 50,
  };
}

const adminUsersSlice = createSlice({
  name: 'adminUsers',

  initialState,

  reducers: {
    /**
     * AdminUsersGrid
     *   ↓ dispatch
     * fetchAdminUsersRequest
     *   ↓
     * adminUsersSaga의 takeLatest가 감지
     */
    fetchAdminUsersRequest(
        state,
        action: PayloadAction<AdminUsersQuery>,
    ) {
      state.loading = true;
      state.error = null;

      state.query = normalizeQuery(
          action.payload,
      );
    },

    /**
     * fetchAdminUsersSaga
     *   ↓ API 성공
     * fetchAdminUsersSuccess
     */
    fetchAdminUsersSuccess(
        state,
        action: PayloadAction<AdminUsersResponse>,
    ) {
      const {
        items,
        total,
        page,
        size,
        pageCount,
      } = action.payload;

      state.loading = false;
      state.loaded = true;
      state.error = null;

      state.items = Array.isArray(items)
          ? items
          : [];

      state.total =
          Number.isFinite(total) &&
          total >= 0
              ? total
              : 0;

      state.pageCount =
          Number.isFinite(pageCount) &&
          pageCount > 0
              ? Math.floor(pageCount)
              : 1;

      /**
       * Backend가 page/size를 최종 보정하기 때문에
       * 성공 응답값을 Redux source of truth에 반영한다.
       */
      state.query.page =
          Number.isFinite(page) &&
          page > 0
              ? Math.floor(page)
              : state.query.page;

      state.query.size =
          Number.isFinite(size) &&
          size > 0
              ? Math.min(
                  200,
                  Math.floor(size),
              )
              : state.query.size;
    },

    /**
     * fetchAdminUsersSaga
     *   ↓ API 실패
     * fetchAdminUsersFailure
     */
    fetchAdminUsersFailure(
        state,
        action: PayloadAction<string>,
    ) {
      state.loading = false;
      state.loaded = true;

      state.error =
          action.payload ||
          '고객 정보 조회에 실패했습니다.';

      /**
       * 기존 정상 조회 데이터는 유지한다.
       * 새로고침 한 번 실패했다고 화면 전체를 0건으로
       * 만들어 버리지 않는다.
       */
    },

    clearAdminUsersError(state) {
      state.error = null;
    },

    resetAdminUsers() {
      return createInitialState();
    },
  },
});

export const {
  fetchAdminUsersRequest,
  fetchAdminUsersSuccess,
  fetchAdminUsersFailure,
  clearAdminUsersError,
  resetAdminUsers,
} = adminUsersSlice.actions;

export default adminUsersSlice.reducer;
